import Controller from '@ember/controller';
import { computed, action } from '@ember/object';
import { readOnly } from '@ember/object/computed';
import { inject as service } from '@ember/service';
import { task, dropTaskGroup, restartableTask, restartableTaskGroup } from 'ember-concurrency';
import { v1 } from 'ember-uuid';
import {
  INVOICES_REPORT_DEFAULT_INCLUDES,
  PAGINATION_DEFAULT_COUNT_PER_PAGE,
} from 'sh-www/constants/routes/reports';
import { SH_INVOICE } from 'sh-www/models/sh-invoice';

/**
 * @type {string[]}
 */
const QUERY_PARAMS = Object.freeze([
  'sort',
  'secondary_sort',
  'payment_state',
  'cost_code',
  'story_code',
]);

/**
 * @param {{}} params
 * @return {string}
 */
const serializeParams = (params) => QUERY_PARAMS.reduce((accum, key) => {
  const value = params[key];

  if (value) {
    accum.push(`${key}=${value}`);
  }

  return accum;
}, []).join('&');

/**
 * @param {Object} queryParams
 * @return {Object}
 */
const invoicesReportCache = (queryParams) => {
  const cachedParams = { ...queryParams };
  const cache = new Map();
  const cacheKey = v1();

  return Object.freeze({
    get sort() {
      return cachedParams.sort || 'final_at';
    },

    get secondarySort() {
      return cachedParams.secondary_sort || 'desc';
    },

    get paymentStatus() {
      return cachedParams.payment_state || null;
    },

    get costCode() {
      return cachedParams.cost_code || null;
    },

    get storyCode() {
      return cachedParams.story_code || null;
    },

    get cacheKey() {
      return cacheKey;
    },

    getPage(page) {
      return cache.get(page);
    },

    setPage(page, value) {
      cache.set(page, value);
      return value;
    },

    isValid(queryParams) {
      return serializeParams(cachedParams) === serializeParams(queryParams);
    },
  });
};

class InvoicesReportController extends Controller {
  queryParams = QUERY_PARAMS;
  sort = null;
  secondary_sort = null;
  payment_state = null;
  cost_code = null;
  story_code = null;
  cache = null;

  shInvoices = [];
  pagination = null;
  countMeta = null;

  companyCostCodes = [];
  companyStoryCodes = [];

  @service('commands/cost-code-commands') costCodeCommandService;
  @service('commands/story-code-commands') storyCodeCommandService;
  @service session;
  @service('commands/sh-invoice-commands') shInvoiceCommandService;
  @service store;

  @dropTaskGroup
  modelUpdates;

  @restartableTaskGroup
  modelInvalidation;

  @readOnly('cache.paymentStatus')
  paymentStatusFilter;

  @readOnly('cache.costCode')
  costCodeFilter;

  @readOnly('cache.storyCode')
  storyCodeFilter;

  @readOnly('cache.sort')
  sortValue;

  @readOnly('cache.secondarySort')
  secondarySortValue;

  @computed('session.currentUser')
  get currentUser() {
    return this.session.currentUser;
  }

  @computed('currentUser.company.canUseCompanyTeams')
  get costCodeIncludes() {
    return this.currentUser.company.canUseCompanyTeams
      ? ['company-teams']
      : [];
  }

  @computed('modelUpdates.isRunning','updateModel.isRunning')
  get loading() {
    return this.modelUpdates.isRunning
      || this.updateModel.isRunning
      || this.reloadCodeCredits.isRunning
      || this.reloadFactoids.isRunning;
  }

  @computed(...QUERY_PARAMS)
  get hasFilterSet() {
    return QUERY_PARAMS.some((param) => this[param]);
  }

  @computed('companyCostCodes.@each.{id,availableCreditInCents}')
  get costCodeIdToCreditRemainingMap() {
    return this.companyCostCodes
      .reduce((accum, { id, availableCreditInCents }) => {
        accum[id] = availableCreditInCents;

        return accum;
      }, {});
  }

  @computed('cost_code','companyCostCodes.[]')
  get selectedCostCodeKey() {
    const costCodeId = this.cost_code;
    return this.companyCostCodes.find(({ id }) => id === costCodeId)?.body ?? null;
  }

  @computed('story_code','companyStoryCodes.[]')
  get selectedStoryCodeKey() {
    const storyCodeId = this.story_code;
    return this.companyStoryCodes.find(({ id }) => id === storyCodeId)?.body ?? null;
  }

  @computed('pagination.total')
  get total() {
    return this.pagination?.total ?? 0;
  }

  @computed('pagination.totalPages')
  get totalPages() {
    return this.pagination?.totalPages ?? 0;
  }

  @computed('pagination.currentPage')
  get currentPage() {
    return this.pagination?.currentPage ?? 0;
  }

  @computed('currentPage')
  get disablePreviousPage() {
    return this.currentPage <= 1;
  }

  @computed('currentPage', 'totalPages')
  get disableNextPage() {
    return this.currentPage >= this.totalPages;
  }

  @computed('currentPage', 'total')
  get pageControlsDisplayString() {
    const { total, currentPage } = this;

    const displayPageMin = (currentPage - 1) * PAGINATION_DEFAULT_COUNT_PER_PAGE + 1;
    const displayPageMax = Math.min(currentPage * PAGINATION_DEFAULT_COUNT_PER_PAGE, total);

    return `Showing ${displayPageMin} - ${displayPageMax} of ${total}`;
  }

  modelIsValid(queryParams) {
    return Boolean(this.cache?.isValid(queryParams));
  }

  canAdvancePage(newPage) {
    return (newPage > 0) && (newPage <= this.totalPages);
  }

  @action
  paymentStatusValueChanged(paymentStatus) {
    this.set('payment_state', paymentStatus);
  }

  @action
  updateCostCodeFilter(costCode) {
    this.set('cost_code', costCode?.id);
  }

  @action
  updateStoryCodeFilter(storyCode) {
    this.set('story_code', storyCode?.id);
  }

  @action
  sortValueChanged(sort) {
    this.setProperties({ sort, secondary_sort: null });
  }

  @action
  secondarySortValueChanged(secondarySort) {
    this.set('secondary_sort', secondarySort);
  }

  @task({ group: 'modelUpdates' })
  *initializeModel(queryParams) {
    this.set('cache', invoicesReportCache(queryParams));
    yield this.updateModel.perform(1);

    // load additional data required for operation
    const { company } = this.session.currentUser;
    const { costCodeIncludes } = this;

    const companyCostCodesRequest = this.costCodeCommandService.getCostCodesForCompany(company, costCodeIncludes, { include_deactivated: true });
    const companyStoryCodesRequest = this.storyCodeCommandService.getStoryCodesForCompany(company, costCodeIncludes, { include_deactivated: true });

    const [ companyCostCodes, companyStoryCodes ] = yield Promise.all([
      companyCostCodesRequest,
      companyStoryCodesRequest,
    ]);

    this.setProperties({
      companyCostCodes,
      companyStoryCodes,
    });
  }

  @task({ group: 'modelUpdates' })
  *updatePage(increment) {
    const newPage = this.currentPage + increment;

    if (this.canAdvancePage(newPage)) {
      const isProjectCompleteEl = document.querySelector('.SH-c-reports-buyer-invoices__table');
      const targetOffset = isProjectCompleteEl
        ? isProjectCompleteEl.getBoundingClientRect().top + window.scrollY - 120
        : 0;

      window.scrollTo({ top: targetOffset });

      yield this.updateModel.perform(newPage);
    }
  }

  @task({ group: 'modelInvalidation' })
  *reloadModel(queryParams) {
    yield this.modelUpdates.cancelAll();
    this.initializeModel.perform(queryParams);
  }

  @task({ group: 'modelInvalidation' })
  *invalidateModel() {
    yield this.modelUpdates.cancelAll();

    const queryParams = QUERY_PARAMS.reduce((accum, param) => {
      accum[param] = null;
      return accum;
    }, { });

    this.setProperties({
      ...queryParams,
      shInvoices: [],
      companyCostCodes: [],
      companyStoryCodes: [],
      pagination: null,
      cache: null,
    });
  }

  @restartableTask
  *reloadCodeCredits() {
    const { company } = this.session.currentUser;
    const { costCodeIncludes } = this;

    const companyCostCodesRequest = this.costCodeCommandService.getCostCodesForCompany(company, costCodeIncludes);
    const companyStoryCodesRequest = this.storyCodeCommandService.getStoryCodesForCompany(company, costCodeIncludes);

    const [ companyCostCodes, companyStoryCodes ] = yield Promise.all([
      companyCostCodesRequest,
      companyStoryCodesRequest,
    ]);

    this.setProperties({
      companyCostCodes,
      companyStoryCodes,
    });
  }

  @restartableTask
  *reloadFactoids() {
    const invoiceFactoids = yield this.store.query('factoid', {
      type: SH_INVOICE.FACTOID_TYPE,
      filter: {
        company_id: this.currentUser.company.id,
      },
    });

    this.set('model.invoiceFactoids', invoiceFactoids);
  }

  /**
   *
   * @param {number} page
   */
  @restartableTask
  *updateModel(page, serveFromCache = true) {
    const cache = this.cache;
    const cachedResponse = cache.getPage(page);

    if (cachedResponse && serveFromCache) {
      this.setProperties({ ...cachedResponse });
      return;
    }

    const filter = {
      company_id: this.currentUser.company.id,
      is_child: false,
    };

    if (cache.paymentStatus) {
      filter.payment_state = cache.paymentStatus;
    }

    if (cache.costCode) {
      filter.cost_code_id = cache.costCode;
    }

    if (cache.storyCode) {
      filter.story_code = cache.storyCode;
    }

    const responseObject = yield this.shInvoiceCommandService.loadShInvoices(
      page,
      PAGINATION_DEFAULT_COUNT_PER_PAGE,
      filter,
      INVOICES_REPORT_DEFAULT_INCLUDES,
      cache.sort,
      true,
      true,
    );

    const { shInvoices, pagination, countMeta } = responseObject;

    cache.setPage(page, { shInvoices, pagination });

    this.setProperties({ shInvoices, pagination, countMeta });
  }
}

export default InvoicesReportController;
